import { Component, OnInit } from '@angular/core';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DrawerModule } from 'primeng/drawer';
import { AccordionModule } from 'primeng/accordion';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { HttpClientModule } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { RoleService, Role, RoleWithPermissions, Permission } from '../../../core/services/role.service';
import { PermissionService } from '../../../core/services/permission.service';
import { AuthService } from '../../../core/services/auth';
import { IconFieldModule } from 'primeng/iconfield';

interface SelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-base-role-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    TagModule,
    DrawerModule,
    AccordionModule,
    SelectModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    HttpClientModule,
    ToastModule,
    ToggleSwitchModule,
    IconFieldModule,
    ConfirmDialogModule,
    TooltipModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './base-role-management.html',
  styleUrl: './base-role-management.scss',
})
export class BaseRoleManagement implements OnInit {
  baseRoles: RoleWithPermissions[] = [];
  permissions: Permission[] = [];
  permissionCategories: string[] = [];
  permissionsByCategory: { [category: string]: Permission[] } = {};
  selectedRole: RoleWithPermissions | null = null;
  drawerVisible: boolean = false;
  currentUserRole: string = '';
  loading = false;
  permissionsLoaded = false;
  
  // Form properties
  roleForm: FormGroup;
  selectedPermissions: { [key: string]: boolean } = {};
  
  // Available base role identifications that can be managed
  baseRoleIdentifications: string[] = ['CSM', 'SM', 'SA', 'A', 'FSR'];

  constructor(
    private roleService: RoleService,
    private permissionService: PermissionService,
    private authService: AuthService,
    private fb: FormBuilder,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {
    this.roleForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3)]],
      identification: [{ value: '', disabled: true }],
      permissions: this.fb.group({})
    });
  }

  ngOnInit(): void {
    this.getCurrentUserRole();
    this.checkAccess();
    this.initializePermissions();
    this.loadBaseRoles();
  }

  private checkAccess(): void {
    if (this.currentUserRole !== 'SUPER_DEV_ADMIN') {
      this.messageService.add({
        severity: 'error',
        summary: 'Access Denied',
        detail: 'Only SUPER_DEV_ADMIN users can access base role management'
      });
      // Optionally redirect or disable functionality
      return;
    }
  }

  private initializePermissions(): void {
    console.log('Initializing permissions...');
    this.permissionService.getAllPermissions().subscribe({
      next: (perms: Permission[]) => {
        console.log('Received permissions:', perms);
        
        if (!Array.isArray(perms)) {
          console.error('Permissions is not an array:', perms);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load permissions - invalid data format'
          });
          return;
        }
        
        if (perms.length === 0) {
          console.warn('No permissions found. You may need to create default permissions first.');
          this.messageService.add({
            severity: 'warn',
            summary: 'No Permissions',
            detail: 'No permissions found in the system. Please create default permissions first.'
          });
        }
        
        this.permissions = perms;
        this.permissionCategories = [...new Set(perms.map((p: Permission) => p.category))];
        this.permissionsByCategory = {};
        for (const cat of this.permissionCategories) {
          this.permissionsByCategory[cat] = perms.filter((p: Permission) => p.category === cat);
        }
        
        console.log('Permission categories:', this.permissionCategories);
        console.log('Permissions by category:', this.permissionsByCategory);
        
        // Initialize selected permissions
        perms.forEach(perm => {
          this.selectedPermissions[perm._id] = false;
        });
        
        // Mark permissions as loaded
        this.permissionsLoaded = true;
      },
      error: (error: any) => {
        console.error('Error loading permissions:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to load permissions: ${error.message || 'Unknown error'}`
        });
      }
    });
  }

  private loadBaseRoles(): void {
    this.loading = true;
    
    // Get base roles using the dedicated endpoint
    this.roleService.getBaseRoles().subscribe({
      next: (roles: RoleWithPermissions[]) => {
        this.baseRoles = roles;
        this.loading = false;
        console.log('Base roles loaded:', this.baseRoles);
      },
      error: (error: any) => {
        console.error('Error loading base roles:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load base roles'
        });
        this.loading = false;
      }
    });
  }

  getCurrentUserRole(): void {
    try {
      const currentUser = this.authService.getLoggedInUser();
      if (currentUser && currentUser.role) {
        this.currentUserRole = currentUser.role.identification || '';
        console.log('Current user role:', this.currentUserRole);
      } else {
        console.warn('No user role found, defaulting to empty string');
        this.currentUserRole = '';
      }
    } catch (error) {
      console.error('Error getting current user role:', error);
      this.currentUserRole = '';
    }
  }

  canAccessBaseRoles(): boolean {
    return this.currentUserRole === 'SUPER_DEV_ADMIN';
  }

  openDrawer(role: RoleWithPermissions): void {
    if (!this.permissionsLoaded || this.permissions.length === 0) {
      console.warn('Permissions not loaded yet. Please wait...');
      this.messageService.add({
        severity: 'warn',
        summary: 'Loading',
        detail: 'Permissions are still loading. Please try again in a moment.'
      });
      return;
    }
    
    console.log('Opening drawer for role:', role.name);
    this.selectedRole = role;
    this.drawerVisible = true;
    this.setupForm(role);
  }

  closeDrawer(): void {
    this.drawerVisible = false;
    this.selectedRole = null;
    this.resetForm();
  }

  private setupForm(role: RoleWithPermissions): void {
    console.log('Setting up form for role:', role.name);
    console.log('Role permissions:', role.permissions);
    
    this.roleForm.patchValue({
      name: role.name,
      identification: role.identification
    });

    // Setup permissions in form
    const permissionsGroup = this.roleForm.get('permissions') as FormGroup;
    
    // Clear existing controls
    Object.keys(permissionsGroup.controls).forEach(key => {
      permissionsGroup.removeControl(key);
    });

    // Add controls for all permissions
    this.permissions.forEach(perm => {
      const hasPermission = role.permissions.some(rolePerm => {
        const match = rolePerm._id === perm._id;
        if (match) {
          console.log(`✓ Role has permission: ${perm.name} (${perm._id})`);
        }
        return match;
      });
      console.log(`Permission ${perm.name} (${perm._id}): ${hasPermission}`);
      permissionsGroup.addControl(perm._id, this.fb.control(hasPermission));
    });
    
    console.log('Form permissions group after setup:', permissionsGroup.value);
  }

  private resetForm(): void {
    this.roleForm.reset();
    const permissionsGroup = this.roleForm.get('permissions') as FormGroup;
    Object.keys(permissionsGroup.controls).forEach(key => {
      permissionsGroup.removeControl(key);
    });
  }

  getPermissionControl(permissionId: string): any {
    const permissionsGroup = this.roleForm.get('permissions') as FormGroup;
    return permissionsGroup.get(permissionId);
  }

  onSubmit(): void {
    if (this.roleForm.valid && this.selectedRole) {
      const formValue = this.roleForm.value;
      const permissionsGroup = this.roleForm.get('permissions') as FormGroup;
      const selectedPermIds = Object.keys(permissionsGroup.value).filter(key => permissionsGroup.get(key)?.value);
      
      const updateData: Partial<Role> = {
        name: formValue.name,
        permissions: selectedPermIds
      };

      this.roleService.updateRole(this.selectedRole._id, updateData).subscribe({
        next: (updatedRole: RoleWithPermissions) => {
          // Update the local role
          const index = this.baseRoles.findIndex(r => r._id === this.selectedRole?._id);
          if (index !== -1) {
            this.baseRoles[index] = updatedRole;
          }
          
          this.closeDrawer();
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Base role updated successfully'
          });
        },
        error: (error: any) => {
          console.error('Error updating role:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update base role'
          });
        }
      });
    }
  }

  isPermissionSelected(permissionId: string): boolean {
    const permissionsGroup = this.roleForm.get('permissions') as FormGroup;
    return permissionsGroup.get(permissionId)?.value || false;
  }

  hasPermission(role: RoleWithPermissions, permissionId: string): boolean {
    return role.permissions.some(perm => perm._id === permissionId);
  }

  getCategoryPermissionCount(role: RoleWithPermissions, category: string): number {
    const categoryPermissions = this.permissionsByCategory[category] || [];
    return categoryPermissions.filter(perm => this.hasPermission(role, perm._id)).length;
  }

  getSelectedPermissionsInCategory(category: string): number {
    const categoryPermissions = this.permissionsByCategory[category] || [];
    const permissionsGroup = this.roleForm.get('permissions') as FormGroup;
    return categoryPermissions.filter(perm => permissionsGroup.get(perm._id)?.value).length;
  }

  getRoleSeverity(identification: string): string {
    switch (identification) {
      case 'CSM': return 'danger';
      case 'SM': return 'warning';
      case 'SA': return 'info';
      case 'A': return 'success';
      case 'FSR': return 'secondary';
      default: return 'info';
    }
  }

  getRoleDescription(identification: string): string {
    switch (identification) {
      case 'CSM': return 'Chief System Manager - Highest level administrator';
      case 'SM': return 'System Manager - System level administrator';
      case 'SA': return 'Super Administrator - Subscriber level administrator';
      case 'A': return 'Administrator - Subscriber administrator';
      case 'FSR': return 'Field Service Representative - Front-line user';
      default: return 'Base role';
    }
  }

  // Get permission display name
  getPermissionDisplayName(permission: Permission): string {
    return permission.name;
  }
}
